/**
 * **写流水之后要失效哪些缓存**（`DR-057` D1.2 · 批次 97）
 *
 * ## 为什么需要这个函数（而不是各处自己列）
 *
 * 一笔流水会同时改变**五个**读模型：账户余额、流水列表、净资产、账户明细、往来/贷款进度。
 * 在 8 个写入口各写一遍清单，必然有一处漏 —— 而漏掉的表现是
 * 「改完账，账户页余额还是旧的」，用户会以为**记账没生效**（比报错更糟：它不报错）。
 *
 * ## 🔴 与 `refetchOnWindowFocus` 的分工
 *
 * - 本函数管**同页内**的即时一致性（mutation 成功 → 立刻重取）；
 * - `main.tsx` 的 `refetchOnWindowFocus: true` 管**跨标签/切窗口回来**：
 *   在别处（另一个浏览器标签、手机端）改了账，切回来必须看到新数。
 *   之前它是 `false` + `staleTime: 10s` ⇒ 切回来 10 秒内仍是旧值。
 */

import type { QueryClient } from '@tanstack/react-query';

/**
 * 写流水（新增 / 改 / 删 / 校准 / 次卡 / 还款 / 结算）成功后调用一次。
 *
 * ⚠️ `invalidateQueries` 是**前缀匹配**：`['transaction']` 不会匹配 `['transactions']`，
 * 反之亦然（项目里单笔详情用 `['transaction', id]`、列表用 `['transactions']`）⇒ 两个都要写。
 */
export function invalidateAfterTxnWrite(qc: QueryClient): void {
  // 余额（账户页 / 首页 / 各选择器）
  void qc.invalidateQueries({ queryKey: ['accounts'] });
  // 流水列表与单笔详情
  void qc.invalidateQueries({ queryKey: ['transactions'] });
  void qc.invalidateQueries({ queryKey: ['transaction'] });
  // 净资产（首页 Hero / 资产页 / 曲线）
  void qc.invalidateQueries({ queryKey: ['networth'] });
  // 账户明细抽屉
  void qc.invalidateQueries({ queryKey: ['accountLedger'] });
  // 往来时间线 / 贷款期次进度 / 必付日历（改一笔会动这些派生量）
  void qc.invalidateQueries({ queryKey: ['contraTimeline'] });
  void qc.invalidateQueries({ queryKey: ['loanProgress'] });
  void qc.invalidateQueries({ queryKey: ['upcoming'] });
}

/**
 * 🆕 批 `107`（K1）：写**手动资产 / 负债**之后要失效哪些缓存。
 *
 * 两个都要：`['net-worth-items']` 是资产页自己那一块列表，
 * `['networth']` 是 J 域算出来的净资产（资产 / 负债分桶 + 首页 Hero）——
 * 手动项**进了** `composeNetWorth`，所以只刷列表不刷净资产，
 * 用户会看到"加了一条 ¥5,000，但净资产一分没变"（典型的双口径错觉）。
 */
export function invalidateAfterNetWorthItemWrite(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['net-worth-items'] });
  void qc.invalidateQueries({ queryKey: ['networth'] });
}

/**
 * 🆕 批 `121`（K4 · `DR-068`）：**切换账本本币**成功后要失效哪些缓存。
 *
 * ## 🔴 为什么是四个 key，而不是交接文档写的三个
 *
 * 交接提示词说「`['ledgers']` / `['networth']` / 报表 key」三处。但**实际 grep 出来的 key 有四个**：
 *
 * | key | 谁在用（grep 实测） | 漏掉的症状 |
 * |---|---|---|
 * | `['ledgers']` | 顶栏币种符号 · `useBaseCurrency()` · 设置页本币 chip | 顶部符号还是旧币 |
 * | `['networth']` | `HomePage` / `AssetsPage` 的现值 | 首页 Hero 数字不跟着变 |
 * | `['networth-curve']` | `NetWorthChart`（**带 `range` 后缀**） | 🔴 **曲线不刷新** |
 * | `['report']` | `ReportPage` 用 `['report', months]` | 报表币种与金额不变 |
 *
 * 🔴 第三条是最容易漏的：`invalidateQueries` 是**前缀匹配**，
 * `['networth']` **不会**命中 `['networth-curve', '12m']`（首元素是不同的字符串）。
 * 只看"净资产的 key 是 `['networth']`"就会以为曲线跟着刷了 —— 它不会。
 */
export function invalidateAfterBaseCurrencyChange(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['ledgers'] });
  void qc.invalidateQueries({ queryKey: ['networth'] });
  // 🔴 单独列：曲线是独立 key（`['networth-curve', range]`），不被 `['networth']` 前缀命中
  void qc.invalidateQueries({ queryKey: ['networth-curve'] });
  void qc.invalidateQueries({ queryKey: ['report'] });
}
